import { useEffect, useMemo, useState } from 'react';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { clampCustomRange, CUSTOM_RANGE_MAX_DAYS, type CustomRangeBounds } from '@busy/api';
import { useT } from '../../lib/i18n';

// Custom-range picker — opens from the RangeSelector's "Custom…" entry.
// Two native `<input type="date">` fields wrap into a sheet; tapping
// Apply sets the dashboard range to 'custom' with the clamped bounds.
//
// Design notes:
//   • Two native date inputs render as iOS wheel pickers on iOS, native
//     dropdowns on Android, and standard date inputs on desktop. No
//     custom calendar widget needed.
//   • Max span enforced via clampCustomRange (CUSTOM_RANGE_MAX_DAYS,
//     currently 366). The Apply button shows a clamp warning when the
//     user's selection got trimmed.
//   • End cannot exceed today. start cannot exceed end.
//   • No vs-prior comparison on custom ranges (dashboard suppresses).

export function CustomRangeSheet({
  initial,
  onApply,
  onClose,
}: {
  /** Bounds to pre-fill — null falls back to "last 30 days." */
  initial?: CustomRangeBounds | null;
  onApply: (bounds: CustomRangeBounds) => void;
  onClose: () => void;
}) {
  const t = useT();

  // Defaults: last 30 days if no prior custom bounds. Pre-fill the
  // previously-applied range so a user tweaking the picker doesn't
  // start from scratch.
  const todayYmd = useMemo(() => ymdLocal(new Date()), []);
  const default30DaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return ymdLocal(d);
  }, []);
  const [start, setStart] = useState<string>(initial?.start ?? default30DaysAgo);
  const [end, setEnd] = useState<string>(initial?.end ?? todayYmd);
  useEffect(() => {
    if (initial) {
      setStart(initial.start);
      setEnd(initial.end);
    }
  }, [initial?.start, initial?.end]);

  // Live clamp preview — surfaces a warning the moment the user picks
  // bounds outside the allowed window so they understand WHY their
  // applied bounds differ.
  const clamped = useMemo(() => clampCustomRange({ start, end }), [start, end]);
  const wasClamped = clamped.start !== start || clamped.end !== end;

  function handleApply() {
    onApply(clamped);
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="sm" ariaLabel={t('customRange.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 pr-12 border-b border-line">
        <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-500 mb-1.5">
          {t('customRange.eyebrow')}
        </div>
        <h2 className="font-display text-2xl leading-tight">{t('customRange.title')}</h2>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[12px] font-semibold text-ink-500 mb-1">
              {t('customRange.startLabel')}
            </span>
            <input
              type="date"
              value={start}
              max={end}
              onChange={(e) => setStart(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-line bg-surface text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-gold-500/40"
            />
          </label>
          <label className="block">
            <span className="block text-[12px] font-semibold text-ink-500 mb-1">
              {t('customRange.endLabel')}
            </span>
            <input
              type="date"
              value={end}
              min={start}
              max={todayYmd}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-line bg-surface text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-gold-500/40"
            />
          </label>
        </div>

        {/* Clamp warning — only renders when the user's selection got
            trimmed to the max-span / today bounds, so the Apply result
            doesn't silently differ from what they picked. */}
        {wasClamped && (
          <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-800 dark:text-amber-200 leading-snug">
            {t('customRange.clamped', { maxDays: CUSTOM_RANGE_MAX_DAYS })}
          </div>
        )}

        <p className="text-[11px] text-ink-500 leading-snug">
          {t('customRange.footnote', { maxDays: CUSTOM_RANGE_MAX_DAYS })}
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 h-11 rounded-lg border border-line bg-surface font-semibold text-ink-700"
          >
            {t('customRange.cancel')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold"
          >
            {t('customRange.apply')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
